// lib/supabase/client.ts

"use client";

import { createBrowserClient } from "@supabase/ssr";
import type { Database } from "./database.types";

const mask = (v?: string | null, head = 4, tail = 4) => {
  if (!v) return "(empty)";
  if (v.length <= head + tail) return v[0] + "***";
  return `${v.slice(0, head)}...${v.slice(-tail)} (len=${v.length})`;
};

// These logs used to fire unconditionally in the browser — including in
// production — leaking the Supabase URL, the publishable-key prefix, and every
// request URL (with user/org ids) into the public console. Now they're muted in
// production by default. Turn them on per-browser for debugging with
// `localStorage.setItem("oneplace:supabase_debug","1")` then reload. Reversible,
// scoped to that one browser, never affects anyone else.
function debugOn(): boolean {
  if (process.env.NODE_ENV !== "production") return true;
  try {
    return typeof window !== "undefined" && localStorage.getItem("oneplace:supabase_debug") === "1";
  } catch {
    return false;
  }
}
function dbg(...args: unknown[]) {
  if (debugOn()) console.debug(...args);
}

export function createClient() {
  const url = process.env.NEXT_PUBLIC_SUPABASE_URL!;
  const anon = process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY!;

  if (typeof window !== "undefined") {
    dbg("[supabase/client] init", {
      NEXT_PUBLIC_SUPABASE_URL: url,
      NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY: mask(anon),
    });
  }

  return createBrowserClient<Database>(url, anon, {
    global: {
      fetch: async (input, init) => {
        const urlStr = typeof input === "string" ? input : (input as Request).url;
        dbg("[supabase/client] fetch →", { url: urlStr, method: init?.method || "GET" });

        const res = await fetch(input, init);

        dbg("[supabase/client] fetch ←", { url: urlStr, status: res.status });
        return res;
      },
    },
  });
}
